import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, inject, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, Validators, UntypedFormControl} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {fuseAnimations} from '@fuse/animations';
import {FuseTranslationLoaderService} from '@fuse/services/translation-loader.service';
import {Store} from '@ngxs/store';
import {TRANSLATION_BASE} from 'app/constants/injection-tokens';
import {NZ_MODAL_DATA, NzModalRef} from 'ng-zorro-antd/modal';
import {NamedListService} from 'app/blocks/service/named-list.service';
import {Resource} from 'app/constants/resource';
import {locale as english} from 'app/common/export-settings/export-settings-modal/i18n/en';
import {locale as french} from 'app/common/export-settings/export-settings-modal/i18n/fr';
import {createSettingsForm, createIndividualSettingsForm} from 'app/common/export-settings/export-settings-modal/export-settings-formgroup';
import {InvoiceService} from 'app/blocks/service/api/invoice.service';
import {BehaviorSubject, Subject, Observable} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {DiTime} from 'app/blocks/util/di-time';
import {HaulierOrderService} from 'app/blocks/service/api/haulier-order.service';
import {ExportService} from 'app/blocks/service/api/export.service';

export interface ExportSettingsModalData {
    /** One export type, or several comma-separated ones to choose from. */
    exportType: string;
    /** Loads and saves the settings here instead of `api/export-settings/<exportType>`. */
    settingsUrl?: string;
    allowCustomColumns?: boolean;
}

@Component({
    // Angular 22 defaults to OnPush; Eager preserves the previous behaviour.
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'export-settings-modal-component',
    templateUrl: './export-settings-modal.component.html',
    providers: [
        {
            provide: TRANSLATION_BASE,
            useValue: Resource.INVOICES.toString()
        }
    ],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    standalone: false
})
export class ExportSettingsModalComponent implements OnInit, OnDestroy {
    /** Angular 22 can drop the zone tick that renders a value assigned from a promise. */
    private readonly _cdr = inject(ChangeDetectorRef);

    exportTypes$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

    entityForm: UntypedFormGroup;
    exportSettingsForm: UntypedFormArray;
    writeSettingsForm: UntypedFormGroup;
    readSettings = [];
    timeSettings = new Set();
    loading = false;

    private _unsubscribeAll: Subject<void> = new Subject();
    private _addDisabledSubject = new BehaviorSubject(false);
    addDisabled$: Observable<boolean> = this._addDisabledSubject.asObservable();

    includeError = false;

    constructor(
        @Inject(NZ_MODAL_DATA) public modalData: ExportSettingsModalData,
        _store: Store,
        _route: ActivatedRoute,
        private _formBuilder: UntypedFormBuilder,
        private _namedList: NamedListService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        public modal: NzModalRef,
        private exportService: ExportService
    ) {
        this.entityForm = createSettingsForm(_formBuilder);
        this.exportSettingsForm = this.entityForm.get('exportSettings') as UntypedFormArray;
        this.writeSettingsForm = this.entityForm.get('writeSettings') as UntypedFormGroup;
        this._fuseTranslationLoaderService.loadTranslations(english, french);

        const exportTypes = modalData.exportType.split(',');
        this.exportTypes$.next(
            exportTypes.map((type) => {
                return {value: type, label: type};
            })
        );
        this.entityForm.patchValue({exportType: exportTypes[0]});
    }

    ngOnInit(): void {
        this.subscribeToFormValidStatus();
        this.loadExportSettings();
    }

    loadExportSettings() {
        this.loading = true;
        this.exportService
            .getExportSettings(this.entityForm.value.exportType, this.modalData.settingsUrl)
            .then((response) => {
                this.loading = false;
                if (response.status === 200) {
                    const settings = response.body.exportSettings;
                    this.readSettings = response.body.readSettings;
                    this.updateExportSettings(settings);
                    this.updateUserSettings(this.readSettings);
                }
                this._cdr.markForCheck();
            })
            .catch((err) => {
                this.loading = false;
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    subscribeToFormValidStatus(): void {
        this.entityForm.statusChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((status) => {
            const formInvalid = status === 'INVALID';
            this._addDisabledSubject.next(formInvalid);
        });
    }

    updateExportSettings(settings: any[]): void {
        if (settings) {
            this.exportSettingsForm.clear();
            settings
                .sort((a, b) => a.position - b.position)
                .forEach((element) => {
                    const settingForm = createIndividualSettingsForm(this._formBuilder);
                    settingForm.patchValue(element);
                    this.exportSettingsForm.push(settingForm);
                });
        }
    }

    addEmptyExportSetting() {
        const settingForm = createIndividualSettingsForm(this._formBuilder);
        settingForm.patchValue({defaultColumnName: 'Custom', include: true, internalColumnName: 'Custom'});
        this.exportSettingsForm.push(settingForm);
    }

    updateUserSettings(readSettings: any[]): void {
        if (!readSettings || readSettings.length === 0) {
            return;
        }

        readSettings.forEach((setting) => {
            let formControl = this.writeSettingsForm.controls[setting.key];
            if (!formControl) {
                formControl = new UntypedFormControl();
                this.setValidators(setting, formControl as UntypedFormControl);
                this.writeSettingsForm.addControl(setting.key, formControl);
            }
            const settingValue = setting.settingValue ? setting.settingValue : setting.defaultValue;
            switch (setting.valueType) {
                case 'BOOLEAN':
                    formControl.patchValue(JSON.parse(settingValue));
                    break;
                case 'TIME':
                    formControl.patchValue(DiTime.newInstance(settingValue).asDate());
                    this.timeSettings.add(setting.key);
                    break;
                default:
                    formControl.patchValue(settingValue);
            }
        });
    }

    saveSettings(): void {
        const includedList = this.exportSettingsForm.value.filter((value: any) => value.include);
        if (this.entityForm.valid) {
            this.loading = true;
            if (includedList && includedList.length > 0) {
                const formValue = this.entityForm.value;
                formValue.exportSettings.forEach((setting: any, index) => {
                    setting.position = index;
                });
                const settings = formValue.writeSettings;
                if (settings) {
                    for (const key of Object.keys(settings)) {
                        if (this.timeSettings.has(key) && settings[key]) {
                            settings[key] = DiTime.newInstance(settings[key]);
                        }
                    }
                }
                this.exportService
                    .saveExportSettings(
                        {
                            exportSettings: formValue.exportSettings,
                            writeSettings: formValue.writeSettings
                        },
                        this.entityForm.value.exportType,
                        this.modalData.settingsUrl
                    )
                    .then(() => {
                        this.loading = false;
                        this.modal.close();
                        this._cdr.markForCheck();
                    })
                    .catch((err) => {
                        this.loading = false;
                    });
            }
        }
    }

    setValidators(setting: any, formControl: UntypedFormControl): void {
        const validators = [];
        if (setting.maxLength !== null) {
            validators.push(Validators.maxLength(setting.maxLength));
        }
        if (setting.minLength !== null) {
            validators.push(Validators.minLength(setting.minLength));
        }
        if (setting.mandatory) {
            validators.push(Validators.required);
        }
        if (setting.maxValue !== null) {
            validators.push(Validators.max(setting.maxValue));
        }
        if (setting.minValue !== null) {
            validators.push(Validators.min(setting.minValue));
        }
        formControl.setValidators(validators);
    }

    drop(event: any) {
        const {previousIndex, currentIndex} = event;
        if (previousIndex !== currentIndex) {
            const elementToReplace = this.exportSettingsForm.at(previousIndex);
            this.exportSettingsForm.removeAt(previousIndex);
            this.exportSettingsForm.insert(currentIndex, elementToReplace);
        }
    }
}
